import { tv, type VariantProps } from './lib/tv';

/**
 * Counter — kit 67:10602 (spec §4.3). Pill with a number; `notify` is the orange header-bell counter.
 */
export const counterStyles = tv({
  base: 'inline-flex shrink-0 items-center justify-center rounded-full font-semibold tabular-nums whitespace-nowrap',
  variants: {
    size: {
      sm: 'h-16 min-w-16 px-4 text-xxs', // 600 10/12
      md: 'h-20 min-w-20 px-4 text-xs', // 600 12/16
      lg: 'h-24 min-w-24 px-6 text-sm', // 600 14/20
    },
    tone: { neutral: '', white: '', accent: '', notify: '', danger: '' },
    variant: { tinted: '', filled: '' },
    ring: { true: 'ring-2 ring-panel' },
  },
  compoundVariants: [
    { tone: 'neutral', variant: 'tinted', class: 'bg-field text-fg-secondary' },
    { tone: 'neutral', variant: 'filled', class: 'bg-secondary-bg-1000 text-white' },
    { tone: 'white', variant: 'tinted', class: 'bg-white-200 text-white' },
    { tone: 'white', variant: 'filled', class: 'bg-white text-primary-bg-1000' },
    { tone: 'accent', variant: 'tinted', class: 'bg-accent-soft text-accent' },
    { tone: 'accent', variant: 'filled', class: 'bg-accent text-on-accent' },
    { tone: 'notify', variant: 'tinted', class: 'bg-orange-200 text-orange-1000' },
    { tone: 'notify', variant: 'filled', class: 'bg-notify text-white' },
    { tone: 'danger', variant: 'tinted', class: 'bg-tone-red-20 text-tone-red' },
    { tone: 'danger', variant: 'filled', class: 'bg-tone-red text-white' },
  ],
  defaultVariants: { size: 'sm', tone: 'neutral', variant: 'tinted' },
});

export interface CounterProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>, VariantProps<typeof counterStyles> {
  value: number | string;
  /** Numbers above `max` render as `${max}+`. */
  max?: number;
}

export function Counter({ value, max, size, tone, variant, ring, className, ...rest }: CounterProps) {
  const text = typeof value === 'number' && max != null && value > max ? `${max}+` : value;
  return (
    <span {...rest} className={counterStyles({ size, tone, variant, ring, class: className })}>
      {text}
    </span>
  );
}
